import { ReactNode } from 'react';
import { Helmet as ReactHelmet } from 'react-helmet';
import useLocalStorage from 'utils/useLocalStorage';
import { useMarketplaceDomain } from 'hooks/useMarketplace';
import { getMarketContent } from 'utils/marketContent';
import crwdmarket_logo from '../../assets/crwd/brand_market/crwdmarket-default-logo.png';
import AMM_logo from '../../assets/advancedmedicine/advancedmedicine-default-logo.png';

type HelmetPropTypes = {
  title?: string;
  description?: string;
  link: string;
  addDarkModeStyle?: boolean;
  keywords?: string | null;
  imageCard?: string;
  largeTwitterCard?: boolean;
  addPostfixTitle?: boolean; // if true, adds POSTFIX_TITLE to the end of the title. Ex/ "Vitamins - Advanced Medicine Marketplace "
  noIndex?: boolean;
  children?: ReactNode | null;
  twitterUsername?: string;
};

const DEFAULT_DESCRIPTION =
  'A blockchain marketplace platform for people passionate about health.';
let DEFAULT_TITLE = 'crwdmarket';
let DEFAULT_IMAGE_CARD = crwdmarket_logo;

export const Head = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  link,
  keywords = '',
  imageCard,
  largeTwitterCard = false,
  addPostfixTitle = false,
  noIndex = false,
  children = null,
  addDarkModeStyle = false,
  twitterUsername = '@CrowdPoint_Tech',
}: HelmetPropTypes) => {
  const market = getMarketContent();
  const data = market.meta;

  // TODO: Remove this once tailwind styling is updated * For DarkMode:
  const [isDarkFromLocalStorage] = useLocalStorage<boolean>(
    'isDark',
    localStorage.getItem('isDark') === 'true',
  );
  const marketplaceDomain = useMarketplaceDomain();

  // default values:
  const DOMAIN =
    marketplaceDomain === 'advancedmedicine'
      ? `https://${marketplaceDomain}.market`
      : 'http://storefront.blockchainecosystem.exchange';
  const MAIN_KEYWORDS =
    'blockchain, blockchain marketplace, blockchain ecosystem, crwdmarket, crowdpoint technology';
  DEFAULT_IMAGE_CARD =
    marketplaceDomain === 'advancedmedicine' ? AMM_logo : crwdmarket_logo;

  const OGIMAGE_SOURCE =
    marketplaceDomain === 'advancedmedicine'
      ? DOMAIN + AMM_logo
      : DOMAIN + crwdmarket_logo;
  DEFAULT_TITLE = data.title || 'crwdmarket';
  const POSTFIX_TITLE = ` - ${data.title}` || ' - crwdmarket';

  let metaTitle: string | undefined;

  if (addPostfixTitle) {
    metaTitle = title + POSTFIX_TITLE;
  } else {
    metaTitle = title;
  }

  const metaDescription = description ?? DEFAULT_DESCRIPTION;
  const metaLink = DOMAIN + link;

  const metaKeywords = keywords?.length
    ? `${MAIN_KEYWORDS}, ${keywords}`
    : MAIN_KEYWORDS;

  let metaImageCard: string;

  if (imageCard) {
    if (imageCard.startsWith('https' || 'http')) {
      metaImageCard = imageCard;
    } else {
      metaImageCard = DOMAIN + imageCard;
    }
  } else {
    metaImageCard = DOMAIN + DEFAULT_IMAGE_CARD;
  }

  const metaRobots = noIndex ? 'noindex, nofollow' : 'index, follow';
  const twitterCardType = largeTwitterCard ? 'summary_large_image' : 'summary';

  return (
    <ReactHelmet>
      <html lang="en" />
      {/* TODO: Fix this for not listed on const market = getMarketContent(); */}
      {marketplaceDomain === 'advancedmedicine' ? (
        <script
          type="text/javascript"
          id="hs-script-loader"
          async
          defer
          src="//js.hs-scripts.com/7175493.js"
        />
      ) : null}

      {data.faviconLight ? (
        <link
          rel="icon"
          type="image/png"
          href={isDarkFromLocalStorage ? data.faviconLight : data.faviconDark}
          sizes="16x16"
        />
      ) : (
        <link
          rel="icon"
          type="image/png"
          href="/crwd-favicon.ico"
          sizes="16x16"
        />
      )}

      {/* OG Tags */}
      <meta property="og:image" content={metaImageCard} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:url" content={metaLink} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={OGIMAGE_SOURCE} />

      {/* //Basic Meta Tags:  */}

      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={metaLink} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content={metaRobots} />
      {/* // * Twitter tags *\\ */}
      <meta property="twitter:site" title={twitterUsername} />
      <meta property="twitter:description" title={metaDescription} />
      <meta property="twitter:title" title={metaTitle} />
      <meta property="twitter:creator" content={twitterUsername} />
      <meta property="twitter:card" content={twitterCardType} />
      <meta property="twitter:image" content={metaImageCard} />
      {/* // Facebook id: app ID in facebook  App Dashboard. */}
      {/* <meta property="fb:app_id" content="add crwd fb id here" /> */}

      <meta property="referrer" content="origin-when-crossorigin" />
      {/* // TODO: Removed once tailwind darkmode is updated */}
      {addDarkModeStyle ? (
        <link
          rel="stylesheet"
          href={
            isDarkFromLocalStorage
              ? '/assets/vela-green/theme.css'
              : '/assets/saga-purple/theme.css'
          }
        />
      ) : null}
      {/* //add customized meta methods via children if needed */}
      {children}
    </ReactHelmet>
  );
};
